import moment from 'moment';
import {trainingAPI} from '../../api/TrainingAPI';
import { 
  getRestTravelLabel,
  getBarColorsForHighcharts,
  getRestAndTravelDaysInChart,
  addPredictionSeriesConfig
} from '../../Utils/chart_utils';
import {getPercentages} from "../../Colors/percentages";
import {getBarColorForDrillData} from "./BarChartMapper";
import { chartExportableOptions } from "../mapperUtil"

export const mapDataForBarAndBubbleChart = ({
  dataIn,
  mainSeriesMetric,
  secondarySeriesMetric,
  teamName,
  showPredictions = false,
  teamColors,
  games = [],
  showRest = false,
  showTravel = false,
  restAndTravel = null,
  startDate = null,
  endDate = null,
  isTeamChart = false,
  showAvgPlotline = false,
  showACWR = false,
  singleAxis = false,
  isBreakdown = false,
  isDrillData = false,
  sortData = true,
  session = [], 
  playerGroup = [],
  position = [],
  ACWRLowerLimit = 0.8,
  ACWRUpperLimit = 1.3,
  showTitle = true,
  customTitle = "",
  enableDrillDown = false,
  showHamburger = true,
  dataColorUtil = undefined,
  selectedPlayerId = null
}) => {
  const acwr_upper_limit = ACWRUpperLimit;
  const acwr_lower_limit = ACWRLowerLimit;
  const categories = [];
  const colors_100 = dataIn?.colors;
  const mainSeriesMetricKey = mainSeriesMetric?.key
  const acwrDataPointKey = mainSeriesMetricKey + "_ACWR";
  const perMinuteDataPointKey = mainSeriesMetricKey + "_per_minute";
  const predictionDataPointKey = mainSeriesMetricKey + "_pre";
  const mainPercentages = getPercentages(mainSeriesMetricKey);
  let data = dataIn?.data || []

  const chartData = {
    mainSeriesData: [],
    secondarySeriesData: [],
    acwrSeriesData: [],
    perMinuteSeriesData: [],
    predictionSeriesData: [],
    title: getChartTitle(customTitle, mainSeriesMetric, secondarySeriesMetric),
    metricName: mainSeriesMetric?.name,
    ACWR: mainSeriesMetric?.ACWR && !showACWR && (!isBreakdown || !isDrillData),
    imperialUnit: mainSeriesMetric?.imperial_unit,
    metricUnit: mainSeriesMetric?.metric_unit
  }

  if(showRest || showTravel) {
    const restAndTravelDates = getRestAndTravelDaysInChart(
      startDate,
      endDate,
      data,
      restAndTravel,
      showRest,
      showTravel
    );

    const maxValue = Math.max(
      ...(data.map((dp) => dp[mainSeriesMetricKey]).filter((x) => x) || [])
    );

    const maxPreValue = Math.max(
      ...(data.map((dp) => dp[mainSeriesMetricKey + "__pre"]).filter((x) => x) || [])
    );
  
    data = data.concat(
      restAndTravelDates.map((dateData) => ({
        ...dateData,
        disp_date: moment(dateData.date).format("MM-DD"),
        [mainSeriesMetricKey]: showPredictions
          ? showPredictions
            ? Math.max(maxValue, maxPreValue)
            : maxValue
          : maxValue,
      }))
    );
  }

  if (sortData) {
    data.sort((a, b) => {
      if(isTeamChart) {
        return a[mainSeriesMetricKey] > b[mainSeriesMetricKey] ? -1 : 1;
      } else {
        return a.date < b.date ? -1 : 1;
      }
    });
  }

  data.forEach( (datapoint, index) => {
    if(isTeamChart) {
      categories.push(datapoint.name);
    } else {
      const opponent = games?.find((x) => x.date === datapoint.date)?.opponent || '';
      categories.push(opponent || moment(datapoint.date).format('MM-DD'));
    }
    const mainSeriesMetricValue = mainSeriesMetric?.key ? datapoint[mainSeriesMetric?.key] : undefined;
    const secondarySeriesMetricValue = secondarySeriesMetric?.key ? datapoint[secondarySeriesMetric?.key] : undefined;
    const acwrValue = datapoint[acwrDataPointKey];
    const perMinuteValue = datapoint[perMinuteDataPointKey];
    const predictionValue = datapoint[predictionDataPointKey];

    const restTravelLabel = getRestTravelLabel(datapoint);
    const dataColor = isDrillData
      ? getBarColorForDrillData(datapoint, mainSeriesMetricKey, mainPercentages, colors_100, teamName, dataColorUtil)
              : getBarColorsForHighcharts(datapoint, mainSeriesMetricKey, false, null, dataColorUtil, datapoint.player_id)
    chartData.mainSeriesData.push({
      name: categories[index],
      y: mainSeriesMetricValue !== undefined ? mainSeriesMetricValue : null,
      color: dataColor,
      restTravelLabel,
      ...(enableDrillDown && {drilldown: `drilldown-${index}`}),
      ...(restTravelLabel
        ? {
            dataLabels: {
              format: '{point.restTravelLabel}',
              rotation: 270,
              color: 'black',
              style: {
                textOutline: 'none'
              }
            },
            opacity: 0.5,
         } : {})
    });

    const isLargeBubbleValue = secondarySeriesMetricValue && secondarySeriesMetricValue.toString().length > 4;
  
          const secondaryColor = getBarColorsForHighcharts(datapoint, secondarySeriesMetric?.key, null, null, dataColorUtil);
    chartData.secondarySeriesData.push({
      y: mainSeriesMetricValue ? mainSeriesMetricValue : null,
      bubbleValue: secondarySeriesMetricValue ? secondarySeriesMetricValue : 0,
      marker: {
          enabled: false
      },
      dataLabels: {
        enabled: true,
        style: {
          textOutline: 'none',
          fontWeight: 'normal',
          fontSize: '11px',
          stroke: secondaryColor
        },
        backgroundColor: 'white',
        borderRadius: 12,
        borderWidth: 1,
        borderColor: 'black',
        color: secondaryColor,
        padding: 5,
        shape: 'circle',
        verticalAlign: 'middle',
        align: 'center',
        y: isLargeBubbleValue ? -22 : -17,
        x: 0,
        useHTML: true,
        formatter: function() {
          return `<div style="${isLargeBubbleValue ? 'width:30px;height:30px;line-height:30px;' : 'width:20px;height:20px;line-height:20px;'}text-align:center;z-index:0;">` + this.point.bubbleValue + '</div>';
        }
      }
    });

    chartData.predictionSeriesData.push(predictionValue !== undefined ? predictionValue : null)

    if(chartData.ACWR) {
      chartData.acwrSeriesData.push(acwrValue !== undefined ? {y: acwrValue, custom: acwrValue} : null);
    } else {
      chartData.perMinuteSeriesData.push(perMinuteValue !== undefined ? {y: perMinuteValue, custom: perMinuteValue} : null);
    }
  })

  const {mainSeriesData, secondarySeriesData, acwrSeriesData, perMinuteSeriesData, predictionSeriesData, title, ACWR, metricName} = chartData;
  const mainSeries = {
    name: mainSeriesMetric?.name,
    type: 'column',
    yAxis: 0,
    data: mainSeriesData,
    colorByPoint: false,
    dataLabels: {
      allowOverlap: true,
      style: {
        textOutline: 'none'
      }
    }
  }
  const secondarySeries = {
    name: `${secondarySeriesMetric?.name}`,
    type: 'scatter',
    yAxis: 0,
    id: 'secondary',
    data: secondarySeriesData,
    selected: false,
    dataLabels: {
        enabled: true,
        allowOverlap: true
    }
  }

  const maxAcwrValue = Math.max(
    ...chartData.acwrSeriesData.map( v => v?.y || 0).filter(v => v), 0
  ) || 0;
  const maxPerMinuteValue = Math.max(
    ...chartData.perMinuteSeriesData.map( v => v?.y || 0).filter(v => v), 0
  ) || 0;
  const maxAcwrSeriesValue = Math.ceil(maxAcwrValue * 1.1);
  const maxPerMinuteSeriesValue = Math.ceil(maxPerMinuteValue * 1.1);
  const maxOfSecondYAxis = showACWR ? maxPerMinuteSeriesValue : maxAcwrSeriesValue
  const seriesForSecondYAxis = {
    name: showACWR ? `${mainSeriesMetric.name} per minute` : 'ACWR',
    type: 'spline',
    yAxis: 1,
    data: showACWR ? perMinuteSeriesData : acwrSeriesData,
    color: 'rgb(70, 170, 197)',
    marker: {
      enabled: true,
      radius: 3,
      symbol: 'circle'
    },
    selected: true,
    showInLegend: singleAxis || (!showACWR && !mainSeriesMetric?.ACWR) ? false : true,
    connectNulls: true
  }

  const allSeries = [
    mainSeries,
    secondarySeries,
    ...(isDrillData ? [] : [seriesForSecondYAxis])
  ];
  let originalSeries = [];
  let positionalAvgPlot = [];
  let teamAvgPlot = [];
  let acwrPlot = [];
  let positionsArray = [];
  let categoriesArray = [];
  let playerColors = {};
  
  let options = {
    chart: {
      type: 'column',
      zoomType: 'xy',
      animation: false,
      events: {
        load: function() {
          try {
            this.isDrilling = false;
            this.originalSeries = JSON.parse(JSON.stringify(this.userOptions.series || []));
          } catch (error) {
            console.error('Error storing original series:', error);
            this.originalSeries = [];
          }
          this.options.custom = this.options.custom || {};
          this.options.custom.title = showTitle ? title : '';
        },
        drilldown: function(e) {
          if (this.isDrilling) return;
          this.isDrilling = true;

          if (!e.seriesOptions) {
            const chart = this;
            const date = data[e.point.index]?.date;
            if (!chart.drilldownLevel) {
              chart.drilldownLevel = 0;
            }

            if (chart.drilldownLevel >= 2) {
              return; 
            }
            this.options.custom = this.options.custom || {};

            if (!chart.drilldownData) {
              chart.hasACWR = false
              chart.showLoading('Loading data ...');
              categoriesArray = [...categories]; 
              trainingAPI.getDrillDownChartData({ 
                date: date,
                position,
                player_group: playerGroup,
                sessions: session,
                player_id: selectedPlayerId || null
              })
                .then(response => {
                  const data = response.player_data;
                  playerColors = response.colors;

                  chart.drilldownData = data;
                  const {drilldowns, positions, posAvg} = createDrilldownStructure(data, mainSeriesMetric, e.point.name, playerColors, enableDrillDown);

                  if (ACWR) {
                    chart.yAxis[1].removePlotLine('acwr_upper');
                    chart.yAxis[1].removePlotLine('acwr_lower');
                  }
                  if (showAvgPlotline) {
                    chart.yAxis[0].removePlotLine('avg');
                    const avg = posAvg ? posAvg.toFixed(1) : 0;
                
                    const plotline = getChartTeamAvgPlotLine({avg});
                    positionalAvgPlot = [plotline];
                
                    chart.yAxis[0].update({
                      plotLines: [...positionalAvgPlot]
                    });
                  }
                  
                  positionsArray = [...positions]

                  const xAxis = chart.xAxis[0];
                  xAxis.categories = positions
                  this.options.custom.currentCategories = positions;
                  this.options.custom.drilldown = "positional";
                  const series = drilldowns[e.point.name];  

                  chart.hideLoading();
                  chart.addSeriesAsDrilldown(e.point, series);
                  // addACWRSeries(chart, series.data, 'Positions');
                  addSecondarySeries(chart, series.data, 'Positions')

                  chart.applyDrilldown();
                  updateYAxisMax(chart, series.data, 0);

                  chart.drilldownLevel++;
                })
                .catch(error => {
                  console.error('Error fetching drill-down data:', error);
                  chart.hideLoading();
                })
                .finally(() => {
                  this.isDrilling = false;
                })
            } else {

              const {drilldowns, names} = createDrilldownStructure(chart.drilldownData, mainSeriesMetric, e.point.name, playerColors);
              
              if (showAvgPlotline && teamAvgPlot.length === 0) {
                teamAvgPlot = [getChartTeamAvgPlotLine({mainSeriesData})];
              }

              if (ACWR && acwrPlot.length === 0) {
                acwrPlot = getACWRPlotLines();
              }

              const series = drilldowns[e.point.name];
              let playerNames = series?.data?.map(item => item.name);
              let playerAvg = series?.data?.map(item => item.y).reduce((sum, y) => sum + y, 0);

              if (ACWR) {
                chart.yAxis[1].removePlotLine('acwr_upper');
                chart.yAxis[1].removePlotLine('acwr_lower');
              }
              if (showAvgPlotline) {
                chart.yAxis[0].removePlotLine('avg');
                const avg = playerAvg ? (playerAvg / (playerNames.length > 0 ? playerNames.length : 0)).toFixed(1) : 0;
            
                const plotline = getChartTeamAvgPlotLine({avg});
            
                chart.yAxis[0].update({
                  plotLines: [plotline]
                });
              }
              const xAxis = chart.xAxis[0];
              xAxis.categories = names
              this.options.custom.currentCategories = xAxis.categories;
              this.options.custom.drilldown = "player";
              chart.addSeriesAsDrilldown(e.point, series);
              // addACWRSeries(chart, series.data, 'Players');
              addSecondarySeries(chart, series.data, 'Players')
              chart.redraw();
              this.isDrilling = false;
              chart.drilldownLevel++;
            }
          }
        },
        drillup: function(e) {
          if (this.isDrilling) return;
          this.isDrilling = true;
          this.options.custom = this.options.custom || {};
          const chart = this;
          chart.drilldownLevel--;

          try{
            if (e.seriesOptions._levelNumber === 1) { 
              const xAxis = chart.xAxis[0];

              xAxis.categories = positionsArray;
              this.options.custom.currentCategories = positionsArray;
              this.options.custom.drilldown = "positional";
              if (ACWR) {
                chart.yAxis[1].removePlotLine('acwr_upper');
                chart.yAxis[1].removePlotLine('acwr_lower');
              }
              if (showAvgPlotline) {
                chart.yAxis[0].removePlotLine('avg');
                chart.yAxis[0].update({
                  plotLines: positionalAvgPlot
                });
              }

              let playersSeries = -1
              chart.series.forEach((serie, index) => {
                if (serie.name === `${secondarySeriesMetric?.name} Players`) {
                  playersSeries = index;
                }
              });

              if (playersSeries !== -1) {
                chart.series[playersSeries].remove(false);
              } else {
                console.log('"Players series" series not found');
              }

              chart.redraw();

            } else if (e.seriesOptions._levelNumber === 0) {

              const chart = this;
              if (!chart) return;
              if (chart.drilldownData) {
                delete chart.drilldownData;
              }

              const xAxis = chart.xAxis?.[0];
              if (xAxis && Array.isArray(categories)) {
                xAxis.categories = categories;
                this.options.custom.currentCategories = categories;
                this.options.custom.drilldown = "";
              }
              
              if (showAvgPlotline) {
                chart.yAxis[0].removePlotLine('avg');
                chart.yAxis[0].update({
                  plotLines: teamAvgPlot
                });
              }
              
              if (ACWR) {
                chart.yAxis[1].removePlotLine('acwr_upper');
                chart.yAxis[1].removePlotLine('acwr_lower');
                chart.yAxis[1].update({
                  plotLines: acwrPlot
                });
              }

              let positionsSeries = -1
              chart.series.forEach((serie, index) => {
                if (serie.name === `${secondarySeriesMetric?.name} Positions`) {
                  positionsSeries = index;
                }
              });

              if (positionsSeries !== -1) {
                chart.series[positionsSeries].remove(false);
              } else {
                console.log('"Positions series" series not found');
              }
            }
          } finally {
            this.isDrilling = false;
          }
        }
      }
    },
    title: {
      text: showTitle ? title : ''
    },
    xAxis: {
      categories,
      gridLineWidth: 2,
      lineColor: '#ccd6eb',  
      lineWidth: 1, 
      labels: categories.length > 20
      ? {
          rotation: -90,
          overflow: 'justify',
          staggerLines: 1,
          step: 0
        }
      : categories.length > 10
      ? {
          rotation: -45,
          overflow: 'justify',
          staggerLines: 1,
          step: 0
        }
      : {
          rotation: 0,
        },
      tickmarkPlacement: categories.length > 10 ? 'on' : 'between',
      tickWidth: 1
    },
    drilldown: {
      activeAxisLabelStyle: {
        cursor: 'default',
        color: 'rgb(51, 51, 51)',
        textDecoration: 'none',
        fontWeight: 500,
        tickmarkPlacement: categories.length > 10 ? 'on' : 'between',
        tickWidth: 1
      },
      activeDataLabelStyle: {
        cursor: 'default',
        color: 'rgb(51, 51, 51)',
        textDecoration: 'none',
      },
      series: []
    },
    yAxis: [
      {
        title: {
          text: ''
        },
        tickAmount: 6,
        plotLines: [],
      },
      {
        title: {
          text: ''
        },
        min: 0,
        opposite: true,
        gridLineWidth: 0,
        plotLines: [],
        id: 'acwr'
      }
    ],
    legend: {
      enabled: true,
      verticalAlign: 'top',
      layout: 'horizontal',
    },
    tooltip: {
      shared: true,
      outside: true,
      formatter: function() {
        if (this.point.isRestOrTravel) {
          return false;
        }
        return `<strong>${this.x}</strong> <br> ${mainSeriesMetric.name}: ${this.y} ${this.point.bubbleValue ? `<br> ${secondarySeriesMetric.name}: ${this.point.bubbleValue}` : ``}
          ${this.points?.[1]?.series?.name ? `<br> ${this.points[1].series.name}: ${this.points[1].y}` : ``}`
      }
    },
    plotOptions: {
      column: {
        borderWidth: 0,
        groupPadding: 0.05,
        pointPadding: 0.05,
        dataLabels: {
          enabled: true,
          inside: true,
          y: categories.length > 20 ? 10 : 0,
          rotation: categories.length > 20 ? 270 : 0,
          style: {
            textOutline: 'none',
          }
        }
      },
      spline: {
        dataLabels: {
          style: {
            textOutline: 'none'
          }
        }
      },
      series: {
        animation: false,
        marker: {
            enabled: true
        },
        events: {
          legendItemClick: function(event) {
            const series = this;
            if(!event?.target?.visible && event?.target?.userOptions?.id === 'secondary') {
              series.update({
                dataLabels: {
                  enabled: false
                }
              })
            }
          }
        }
      }
    },
    colors: teamColors,
    series: allSeries,
    exporting: {
      enabled: showHamburger,
      ...chartExportableOptions
    }
  };

  function updateYAxisMax(chart, seriesData, index) {

    const maxValue = Math.max(...seriesData.map(point => point.y));
    const newMax = maxValue * 1.1;
    chart.yAxis[index].setExtremes(null, newMax);
  }

  function addACWRSeries(chart, data, type) {

    const validData = data
      .map(item => {
        if (item?.acwr === undefined || item?.acwr === null) return null;
        return {
          name: item?.name,
          y: item.acwr
        };
      })
      .filter(Boolean);

    if (validData.length === 0) return;

    const acwrSeries = {
      name: `${type} ACWR`,
      type: 'spline',
      yAxis: 1,
      data: validData,
      color: 'rgb(70, 170, 197)',
      selected: false,
      connectNulls: true,
    };

    chart.addSeries(acwrSeries, false);
    // updateYAxisMax(chart, acwrSeries.data, 1);

    chart.hasACWR = true
    chart.redraw();
  }

  function addSecondarySeries(chart, data, type) {

    const secondarySeries = {
      name: `${secondarySeriesMetric?.name} ${type}`,
      type: 'scatter',
      id: 'secondary',
      yAxis: 0,
      data: data.map(item => {
        let bubbleItem;
        if ("secondarySeriesVal" in item) bubbleItem = {...item, [secondarySeriesMetric?.key]: item?.secondarySeriesVal};
        else bubbleItem = {...item};

        const isLargeBubbleValue = item?.secondarySeriesVal && item?.secondarySeriesVal.toString().length > 4;
        return {
          name: item?.name,
          y: item?.y,
          bubbleValue: item?.secondarySeriesVal ? item?.secondarySeriesVal : 0,
          marker: {
            enabled: false
          },
          dataLabels: {
            enabled: true,
            style: {
              textOutline: 'none',
              fontWeight: 'normal',
              fontSize: '11px',
              stroke: getBarColorsForHighcharts(bubbleItem, secondarySeriesMetric?.key, null, null, dataColorUtil)
            },
            backgroundColor: 'white',
            borderRadius: 12,
            borderWidth: 1,
            borderColor: 'black',
            color: getBarColorsForHighcharts(bubbleItem, secondarySeriesMetric?.key, null, null, dataColorUtil),
            padding: 5,
            shape: 'circle',
            verticalAlign: 'middle',
            align: 'center',
            y: isLargeBubbleValue ? -22 : -17,
            x: 0,
            useHTML: true,
            formatter: function() {
              return `<div style="${isLargeBubbleValue ? 'width:30px;height:30px;line-height:30px;' : 'width:20px;height:20px;line-height:20px;'}text-align:center;z-index:0;">` + this.point.bubbleValue + '</div>';
            }
          }
      }}),
      color: 'rgb(70, 170, 197)',
      selected: false,    
      dataLabels: {
        enabled: true,
        allowOverlap: true
    }
    };
    chart.addSeries(secondarySeries, false);
    chart.redraw();
  }

  function createDrilldownStructure(data, metric, date, colors = null, enableDrillDown = false) {
    const drilldowns = {};
    const positions = [...new Set(data.map(item => item.player_id__position))];
    const acwrKey = `${metric.key}_ACWR`;
    const secondarySeriesKey = secondarySeriesMetric?.key;
    const mainSeriesDecimal = mainSeriesMetric?.decimal_places || 2;
    const secondarySeriesDecimal = secondarySeriesMetric?.decimal_places || 2;
    let posSum = 0;

    // Second layer: Positions
    drilldowns[date] = {
      name: 'Positions',
      id: date,
      data: positions.map((position, index) => {
        const positionData = data.filter(item => item.player_id__position === position);
        const avgValue = positionData.reduce((sum, item) => sum + item[metric.key], 0) / positionData.length;

        const hasAcwr = positionData.some(item => item[acwrKey] !== undefined);
        const hasSecondarySeries = positionData.some(item => item[secondarySeriesKey] !== undefined);

        const avgAcwr = hasAcwr ? 
          positionData.reduce((sum, item) => sum + (item[acwrKey] || 0), 0) / positionData.length : 
          null;

        const avgSecondarySeries = hasSecondarySeries ? 
          positionData.reduce((sum, item) => sum + (item[secondarySeriesKey] || 0), 0) / positionData.length :
          null;

        posSum += Number(avgValue?.toFixed(mainSeriesDecimal));

        const dataPoint = {
          name: position,
          y: Number(avgValue?.toFixed(mainSeriesDecimal)),
          ...(enableDrillDown && {drilldown: position}),
          acwr: null,
          color: getBarColorsForHighcharts({[metric.key]: avgValue}, metric.key, false, null, dataColorUtil)
        };
        if (typeof avgAcwr === 'number') {
          const acwrVal = Number(avgAcwr?.toFixed(mainSeriesDecimal));
          if (typeof acwrVal === 'number' && !isNaN(acwrVal)) dataPoint.acwr = acwrVal;
        }

        if (typeof avgSecondarySeries === 'number') {
          const secondaryVal = Number(avgSecondarySeries?.toFixed(secondarySeriesDecimal));
          if (typeof secondaryVal === 'number' && !isNaN(secondaryVal)) dataPoint.secondarySeriesVal = secondaryVal;
        }

        return dataPoint;
      })
     };

    let names = []
    // Third layer: Players
    positions.forEach((position) => {
      const playersData = data
        .filter(item => item.player_id__position === position)
        .map(player => {
          const playerName = `${player.player_id__first_name[0]}. ${player.player_id__last_name}`;
          names.push(playerName);

          const dataPoint = {
            name: playerName,
            y: Number(player[metric.key]?.toFixed(mainSeriesDecimal)),
            color: getBarColorsForHighcharts(player, metric.key, false, null, dataColorUtil),
          };

          if (typeof player[acwrKey] === 'number') {
            const acwrVal = Number(player[acwrKey]?.toFixed(mainSeriesDecimal));
            if (typeof acwrVal === 'number' && !isNaN(acwrVal)) dataPoint.acwr = acwrVal;
          }

          if (typeof player[secondarySeriesKey] === 'number') {
            const secondaryVal = Number(player[secondarySeriesKey]?.toFixed(secondarySeriesDecimal));
            if (typeof secondaryVal === 'number' && !isNaN(secondaryVal)) dataPoint.secondarySeriesVal = secondaryVal;
          }

          return dataPoint;
        });

      drilldowns[position] = {
        name: `Players`,
        acwr: null,
        id: position,
        data: playersData
      };
    });

    const posAvg = posSum / (positions.length > 0 ? positions.length : 100);

    return{ drilldowns, positions, posAvg , names};
  }

  function getACWRPlotLines() {
    return [{
      id: 'acwr_upper',
      dashStyle: 'ShortDash',
      color: 'red',
      value: acwr_upper_limit,
      zIndex: 5
    },{
      id: 'acwr_lower',
      dashStyle: 'ShortDash',
      color: 'green',
      value: acwr_lower_limit,
      zIndex: 5
    }]
  }

  if(ACWR && !isDrillData) {
    const ACWRPlotlines = getACWRPlotLines();

    const secondYAxis = {
      title: {
        text: ''
      },
      min: maxOfSecondYAxis < 0 ? maxOfSecondYAxis : 0,
      max: maxOfSecondYAxis < 0 ? 0 : maxOfSecondYAxis,
      opposite: true,
      gridLineWidth: 0,
      id: 'acwr',
      plotLines: ACWRPlotlines
    }
    acwrPlot = [...ACWRPlotlines];
    options.yAxis[1] = secondYAxis;
  }

  if(showPredictions) {
    options = addPredictionSeriesConfig(options, mainSeriesMetric?.name, mainSeriesMetricKey, predictionSeriesData, mainSeriesData, colors_100, dataColorUtil)
  }

  function getChartTeamAvgPlotLine({ avg, mainSeriesData }) {
    if (!avg) {
      avg = mainSeriesData.length ? (mainSeriesData.reduce((a, b) => a + b.y, 0) / mainSeriesData.length).toFixed(1) : 0;
    }

    const plotline = {
      id: 'avg',
      dashStyle: 'Dash',
      color: 'black',
      value: avg,
      zIndex: 5,
      label: {
        text: `Avg: ${avg}`,
        align: 'right',
        style: {
          zIndex: 60001 
        }
      }
    }
    return plotline;
  }

  if (showAvgPlotline) {
    const plotline = getChartTeamAvgPlotLine({mainSeriesData});
    teamAvgPlot = [plotline];
    options.yAxis[0].plotLines.push(plotline);
  }

  return options;
}

const getChartTitle = (title, mainSeriesMetric, secondarySeriesMetric) => {
  if(title) {
    return title
  }

  return `${mainSeriesMetric?.name} ${secondarySeriesMetric?.name ? `(${secondarySeriesMetric?.name})` : ``}`
}